/**
 * JS mirror of the colour tokens in `styles/theme.css`.
 *
 * WHY THIS EXISTS: everywhere the browser does CSS, the right answer is a token — `text-danger-600`
 * or `var(--color-danger-500)`. But ECharts draws to a <canvas>, and a canvas has no CSS cascade, so
 * `var(--color-danger-500)` there resolves to nothing and the series renders black. Chart palettes
 * therefore need REAL hex values, which is exactly how ~250 raw hexes ended up scattered across the
 * dashboard pages in the first place.
 *
 * So: one file holds those values, named after the tokens they mirror. A chart references
 * `PALETTE.danger[500]` instead of typing `#D9363E`, and the meaning of a colour is decided in one
 * place rather than re-guessed per page (the audit found four different reds and three greens).
 *
 * KEEPING IT HONEST: these literals must match the `@theme` block in `styles/theme.css`. That file
 * is the source of truth; this is the mirror. If you change a token there, change it here.
 * Anything that is NOT on a canvas should use the CSS token directly and ignore this file.
 */

export const PALETTE = {
  brand: { 50: '#edf8e7', 100: '#d4f1c8', 300: '#80ce63', 400: '#5bbb3e', 500: '#45a82b', 600: '#339022', 700: '#297b2b', 800: '#1b6a33' },
  harvest: { 50: '#fdf6e3', 300: '#ecc451', 400: '#e7b22c', 500: '#cf9a1d', 600: '#a87a16' },
  sky: { 400: '#4596ff', 500: '#1677ff', 600: '#0e5fd8' },
  plum: { 400: '#9254de', 500: '#722ed1', 600: '#531dab' },
  earth: { 400: '#97633f', 500: '#6b4226', 600: '#58351e' },
  success: { 500: '#52c41a', 600: '#3fa10f' },
  warning: { 500: '#e7b22c', 600: '#c4921c' },
  danger: { 500: '#d9363e', 600: '#b51d29' },
} as const;

/**
 * Semantic aliases — reach for these in status maps so a page states WHAT a colour means rather
 * than which swatch it picked. `neutral` is theme-dependent, so it is a function of the mode.
 */
export const STATUS_COLOR = {
  info: PALETTE.sky[500],
  progress: PALETTE.harvest[400],
  success: PALETTE.brand[600],
  positive: PALETTE.brand[600],
  warning: PALETTE.warning[600],
  danger: PALETTE.danger[500],
  critical: PALETTE.danger[600],
  special: PALETTE.plum[500],
} as const;

/**
 * Categorical series ramp for multi-series charts — mirrors `--color-chart-1..8` in theme.css.
 * Ordered so adjacent series stay distinguishable; use it in order rather than hand-picking.
 */
export const CHART_SERIES = [
  PALETTE.brand[600], PALETTE.harvest[400], PALETTE.sky[500], PALETTE.brand[300],
  PALETTE.harvest[600], PALETTE.earth[500], PALETTE.brand[500], PALETTE.sky[400],
] as const;

/**
 * IDENTITY ramp — the hues a category MARK may take (a bar per warehouse, a chip per territory).
 *
 * Deliberately NOT `CHART_SERIES`. That list mirrors `--color-chart-1..8` faithfully, and two of its
 * members — deep gold `harvest[600]` and earth brown `earth[500]` — are dark, low-chroma colours that
 * work as one line among eight but read as a muddy smear when they fill a whole bar. This ramp keeps
 * only hues that stay bright and legible at large fill, on both themes.
 */
export const IDENTITY_SERIES = [
  PALETTE.brand[500],    // green
  PALETTE.sky[500],      // blue
  PALETTE.harvest[400],  // gold
  PALETTE.plum[400],     // violet
  PALETTE.brand[300],    // light leaf
  PALETTE.sky[400],      // light blue
  PALETTE.success[500],  // bright lime
] as const;
// No EARTH tone here on purpose: both browns go muddy and dull once they fill a whole bar, however
// well they read as a thin line. No DANGER red either — red means "a problem" everywhere else in the
// app, and an identity colour must not imply a status the row does not have.

/**
 * Colours for a whole set of named categories at once — `{ 'WH-MUM': '#…', 'WH-DEL': '#…' }`.
 *
 * Assigning by RANK within the sorted key set, rather than by a hash of each name, because the job
 * here is telling bars apart. A hash gives no guarantee: with seven hues and six warehouses whose
 * names all begin "WH-", three of them collided on the same green — which is exactly the flat picture
 * the colour was meant to fix. Rank guarantees every entry differs while the set fits the ramp.
 *
 * It is still stable in the way that matters: the colour depends on the SET of names, not on the
 * order the rows arrive in, so a warehouse overtaking another in value does not make them swap hues
 * mid-session. Adding or removing a site does re-deal the colours — an honest trade for bars that can
 * actually be distinguished.
 */
export function seriesColorsFor(keys: readonly string[]): Record<string, string> {
  const sorted = [...new Set(keys.map((k) => String(k ?? '')))].sort();
  const out: Record<string, string> = {};
  sorted.forEach((k, i) => { out[k] = IDENTITY_SERIES[i % IDENTITY_SERIES.length]; });
  return out;
}

/**
 * The real colour value behind a ui `TagTone` — for the places that need a COLOUR and not a class:
 * an inline tint on a control, a selected chip in a Select, a canvas.
 *
 * It lives here rather than in a page because two registers had already written their own copy of it,
 * and a status that is gold on one filter bar and amber on the next is a colour that has stopped
 * meaning anything.
 */
export const TAG_TONE_HUE: Record<string, string> = {
  neutral: PALETTE.earth[400],
  green: PALETTE.brand[600],
  gold: PALETTE.harvest[500],
  blue: PALETTE.sky[500],
  red: PALETTE.danger[500],
  purple: PALETTE.plum[500],
  cyan: PALETTE.sky[400],
};

/**
 * AN ACTIVE FILTER WEARS THE COLOUR OF WHAT IT SELECTED.
 *
 * A toolbar of identical grey controls says nothing about which of them is currently narrowing the
 * list — the only way to find out is to read every placeholder. A set control takes the hue of its
 * own subject instead: the warehouse's identity colour, the product's packaging accent, the status'
 * own tone. It is the same colour that value wears in the rows below, so the control and the list
 * agree.
 *
 * Applied as an inline STYLE, not a class: `cn()` carries no tailwind-merge, so a `bg-…` passed to a
 * component whose own default is `bg-surface` wins or loses by stylesheet order rather than by
 * intent. A style attribute is unambiguous. (And the hue must be a real value, never a `var(--…)`
 * token — Tailwind v4 only emits the theme vars a CLASS references.)
 */
export const filterTint = (hue?: string | null) => (hue ? {
  backgroundColor: `color-mix(in srgb, ${hue} 12%, var(--gb-surface))`,
  borderColor: `color-mix(in srgb, ${hue} 45%, transparent)`,
} : undefined);

/** Muted/neutral greys DO flip with the theme, so they can't be constants. */
export const neutralColor = (dark: boolean) => (dark ? '#9ba49c' : '#5d6a61');
export const surfaceColor = (dark: boolean) => (dark ? '#1f2621' : '#ffffff');
export const gridColor = (dark: boolean) => (dark ? '#2e362f' : '#e1e7dd');

/**
 * Black-or-white ink for text painted ON TOP of a filled colour — a treemap tile, a chart label
 * inside a bar. A canvas has no CSS cascade, so the label colour has to be computed from the fill
 * rather than inherited: the product accents run from a dark green to a bright gold, and one fixed
 * ink is unreadable on half of them. Picks whichever of the two inks has the better WCAG contrast
 * against the fill, so the answer is right for a colour nobody checked by hand.
 */
export function readableInkOn(fill: string): string {
  const h = String(fill).replace('#', '').trim();
  const hex = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = Number.parseInt(hex, 16);
  if (!Number.isFinite(n) || hex.length !== 6) return '#ffffff';
  const lin = (c: number) => { const s = c / 255; return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4; };
  const L = 0.2126 * lin((n >> 16) & 255) + 0.7152 * lin((n >> 8) & 255) + 0.0722 * lin(n & 255);
  const INK = '#101512';           // near-black, matches the app's darkest text
  const inkL = 0.0055;
  const onWhite = 1.05 / (L + 0.05);
  const onInk = (L + 0.05) / (inkL + 0.05);
  return onInk > onWhite ? INK : '#ffffff';
}
